import {
  Children,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";

interface Phrase {
  key: number;
  text: string;
  animate: boolean;
}

/** Ignore tiny suffixes when detecting "this text already faded in another node". */
const ALREADY_SHOWN_MIN = 4;

/** Word tokens that keep their trailing space: `"The "`, `"user "`. */
export const tokenizeNodeText = (text: string): string[] => {
  if (!text) return [];
  const lead = text.match(/^\s+/)?.[0];
  const rest = lead ? text.slice(lead.length) : text;
  const words = rest.match(/\S+\s*/g) ?? [];
  return lead ? [lead, ...words] : words;
};

const recordShown = (
  shownTextsRef: MutableRefObject<string[]> | undefined,
  value: string,
) => {
  if (!shownTextsRef || !value) return;
  const list = shownTextsRef.current;
  if (list[list.length - 1] === value) return;
  list.push(value);
  if (list.length > 80) list.splice(0, list.length - 40);
};

const alreadyShown = (
  shownTextsRef: MutableRefObject<string[]> | undefined,
  value: string,
): boolean => {
  if (!shownTextsRef || value.length < ALREADY_SHOWN_MIN) return false;
  return shownTextsRef.current.some(
    (seen) => seen.length > value.length && seen.endsWith(value),
  );
};

/**
 * Reuse span keys for tokens that are still the same or that only grew
 * (`"The"` → `"The "`). New tokens fade in; a remounted node that already
 * appeared elsewhere does not.
 */
const reconcilePhrases = (
  phrases: Phrase[],
  tokens: string[],
  nextKey: () => number,
  skipFade: boolean,
): Phrase[] => {
  const next: Phrase[] = [];
  let i = 0;
  while (
    i < phrases.length &&
    i < tokens.length &&
    phrases[i].text === tokens[i]
  ) {
    next.push(phrases[i]);
    i++;
  }

  if (i < phrases.length && i < tokens.length) {
    const prev = phrases[i].text;
    const token = tokens[i];
    if (token.startsWith(prev) || prev.startsWith(token)) {
      phrases[i].text = token;
      next.push(phrases[i]);
      i++;
    }
  }

  for (; i < tokens.length; i++) {
    next.push({
      key: nextKey(),
      text: tokens[i],
      animate: !skipFade,
    });
  }
  return next;
};

/**
 * Growing text updates the last word span in place, then appends new word
 * spans. Markdown re-parses must reuse the same span nodes for already-visible
 * words — swapping them for a Fragment or a new key makes that text blink.
 */
const StreamingPhrases = ({
  text,
  shownTextsRef,
}: {
  text: string;
  shownTextsRef?: MutableRefObject<string[]>;
}) => {
  const phrasesRef = useRef<Phrase[]>([]);
  const prevRef = useRef("");
  const nextKeyRef = useRef(0);

  const prev = prevRef.current;
  if (text !== prev) {
    const skipFade = !prev && alreadyShown(shownTextsRef, text);
    phrasesRef.current = reconcilePhrases(
      phrasesRef.current,
      tokenizeNodeText(text),
      () => nextKeyRef.current++,
      skipFade,
    );
    recordShown(shownTextsRef, prev);
    recordShown(shownTextsRef, text);
    prevRef.current = text;
  }

  return phrasesRef.current.map((p) => (
    <span key={p.key} className={p.animate ? "stream-token" : undefined}>
      {p.text}
    </span>
  ));
};

export const wrapStreamingText = (
  children: ReactNode,
  shownTextsRef?: MutableRefObject<string[]>,
): ReactNode =>
  Children.map(children, (child, i) => {
    if (typeof child !== "string" || child.length === 0) return child;
    return (
      <StreamingPhrases
        key={`stream-${i}`}
        text={child}
        shownTextsRef={shownTextsRef}
      />
    );
  });
